import { initializeDB } from '../config/db';
import { TBT_SUBMISSION_STORE } from '../config/constants';
import axiosInstance from '../axiosInstance';

class TBTSubmissionService {
    /**
     * List all incomplete TBT submissions
     */
    async listIncomplete() {
        const db = await initializeDB();
        const tx = db.transaction(TBT_SUBMISSION_STORE, 'readonly');
        const store = tx.objectStore(TBT_SUBMISSION_STORE);
        const statusIndex = store.index('status');

        // Get drafts and in-progress submissions
        const drafts = await statusIndex.getAll('draft');
        const inProgress = await statusIndex.getAll('in_progress');

        await tx.done;

        return [...drafts, ...inProgress];
    }

    /**
     * Get a single TBT submission by ID
     */
    async getById(id) {
        const db = await initializeDB();
        return db.get(TBT_SUBMISSION_STORE, id);
    }

    /**
     * Create a new TBT submission (LOCAL ONLY - no backend call)
     */
    async create(topicId, topicTitle) {
        try {
            // Create locally in IndexedDB only
            const db = await initializeDB();

            // Generate local ID using timestamp
            const localId = `tbt_${Date.now()}`;

            const submission = {
                id: localId,
                topicId: topicId,
                topicTitle: topicTitle,
                employees: [],
                status: 'draft',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                backendSynced: false
            };

            await db.put(TBT_SUBMISSION_STORE, submission);

            return submission;
        } catch (error) {
            console.error('Error creating Tool Box Talk:', error);
            throw new Error('Failed to create Tool Box Talk locally');
        }
    }

    /**
     * Update a TBT submission (LOCAL ONLY - no backend call)
     */
    async update(id, updates) {
        try {
            // Update locally in IndexedDB only
            const db = await initializeDB();
            const existing = await db.get(TBT_SUBMISSION_STORE, id);

            if (!existing) {
                throw new Error('TBT submission not found in local storage');
            }

            const updated = {
                ...existing,
                ...updates,
                updatedAt: new Date().toISOString()
            };

            await db.put(TBT_SUBMISSION_STORE, updated);

            return updated;
        } catch (error) {
            console.error('Error updating Tool Box Talk:', error);
            throw new Error('Failed to update Tool Box Talk locally');
        }
    }

    /**
     * Add an employee to a TBT submission
     */
    async addEmployee(id, employee) {
        const db = await initializeDB();
        const tx = db.transaction(TBT_SUBMISSION_STORE, 'readwrite');
        const store = tx.objectStore(TBT_SUBMISSION_STORE);

        const existing = await store.get(id);
        if (!existing) {
            throw new Error('TBT submission not found');
        }

        const employees = existing.employees || [];
        employees.push({
            ...employee,
            signature: null,
            signedAt: null
        });

        const updated = {
            ...existing,
            employees,
            updatedAt: new Date().toISOString()
        };

        await store.put(updated);
        await tx.done;

        return updated;
    }

    /**
     * Sign for an employee
     */
    async signEmployee(id, employeeIndex, signature) {
        const db = await initializeDB();
        const tx = db.transaction(TBT_SUBMISSION_STORE, 'readwrite');
        const store = tx.objectStore(TBT_SUBMISSION_STORE);

        const existing = await store.get(id);
        if (!existing) {
            throw new Error('TBT submission not found');
        }

        const employees = existing.employees || [];
        if (employeeIndex < 0 || employeeIndex >= employees.length) {
            throw new Error('Employee index out of range');
        }

        // If signature is null, remove it (for Remove Signature button)
        if (signature === null) {
            employees[employeeIndex].signature = null;
            employees[employeeIndex].signedAt = null;
        } else {
            employees[employeeIndex].signature = signature;
            employees[employeeIndex].signedAt = new Date().toISOString();
        }

        // Update status to in_progress if it was draft
        const status = existing.status === 'draft' ? 'in_progress' : existing.status;

        const updated = {
            ...existing,
            employees,
            status,
            updatedAt: new Date().toISOString()
        };

        await store.put(updated);
        await tx.done;

        return updated;
    }

    /**
     * Submit a TBT to backend server
     */
    async submit(id) {
        // Check internet connection first
        if (!navigator.onLine) {
            throw new Error('No internet connection. Please check your connection and try again later.');
        }

        const db = await initializeDB();
        const existing = await db.get(TBT_SUBMISSION_STORE, id);

        if (!existing) {
            throw new Error('TBT submission not found');
        }

        // Validate that there is at least one signature
        const employees = existing.employees || [];
        const hasSignatures = employees.some(emp => emp.signature);

        if (!hasSignatures) {
            throw new Error('Cannot submit: At least one employee must sign the Tool Box Talk');
        }

        try {
            // Submit to backend with all data
            const response = await axiosInstance.post('/toolboxtalk/submit', {
                topicId: existing.topicId,
                topicTitle: existing.topicTitle,
                employees: existing.employees
            });

            if (response.data.success) {
                // Update local record on success
                const tx = db.transaction(TBT_SUBMISSION_STORE, 'readwrite');
                const store = tx.objectStore(TBT_SUBMISSION_STORE);

                const updated = {
                    ...existing,
                    status: 'submitted',
                    submittedAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    backendSynced: true
                };

                await store.put(updated);
                await tx.done;

                return {
                    success: true,
                    message: response.data.message || 'Tool Box Talk submitted successfully',
                    data: updated
                };
            } else {
                throw new Error(response.data.error || 'Failed to submit Tool Box Talk');
            }
        } catch (error) {
            console.error('Error submitting Tool Box Talk:', error);

            // Check if error is network-related
            if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error') || !navigator.onLine) {
                throw new Error('No internet connection. Please check your connection and try again later.');
            }

            // Extract error message from response
            const errorMessage = error.response?.data?.error || error.message || 'Failed to submit Tool Box Talk';

            throw new Error(errorMessage);
        }
    }

    /**
     * Delete a TBT submission
     */
    async delete(id) {
        const db = await initializeDB();
        await db.delete(TBT_SUBMISSION_STORE, id);
    }
}

export default new TBTSubmissionService();
